import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Eye,
  MousePointer,
  Share2,
  Link2,
  Users,
  Clock,
  Loader2,
  AlertTriangle,
  Activity,
  BarChart2,
  MessageSquare,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { fetchNewsletterAnalytics, fetchPollResults } from "../services/api";
import { StatCard, Table } from "../components/ui";
import { useTenant } from "../contexts/TenantContext";

function DepthBar({ categories, totalSent }) {
  if (!totalSent) return null;
  const { noOpen = 0, openOnly = 0, openAndClick = 0 } = categories;
  const pctNo = ((noOpen / totalSent) * 100).toFixed(0);
  const pctOpen = ((openOnly / totalSent) * 100).toFixed(0);
  const pctClick = ((openAndClick / totalSent) * 100).toFixed(0);

  const segments = [
    { label: `No Open (${pctNo}%)`, value: noOpen, color: colors.textSecondary },
    { label: `Opened Only (${pctOpen}%)`, value: openOnly, color: colors.warning },
    { label: `Opened + Clicked (${pctClick}%)`, value: openAndClick, color: colors.success },
  ];

  return (
    <div>
      <div style={{ display: "flex", height: 28, borderRadius: 6, overflow: "hidden", marginBottom: 8 }}>
        {segments.map((s, i) =>
          s.value > 0 ? (
            <div
              key={i}
              style={{
                width: `${(s.value / totalSent) * 100}%`,
                background: s.color,
                opacity: 0.85,
                transition: "width 0.4s",
              }}
            />
          ) : null,
        )}
      </div>
      <div style={{ display: "flex", gap: 16, fontSize: 12, color: colors.textSecondary }}>
        {segments.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: "inline-block" }} />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function truncateUrl(url, max = 60) {
  if (!url || url.length <= max) return url;
  return url.slice(0, max - 3) + "...";
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function NewsletterAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tenant = useTenant();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollData, setPollData] = useState(null);

  useEffect(() => {
    fetchNewsletterAnalytics(id)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    fetchPollResults(id)
      .then(setPollData)
      .catch(() => {}); // polls are optional
  }, [id]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Loader2 size={24} color={colors.textSecondary} className="spin" />      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: colors.danger }}>
        <AlertTriangle size={32} style={{ marginBottom: 8 }} />
        <div>{error}</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => navigate("/newsletters")}
          style={{
            background: "none",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            padding: "6px 10px",
            cursor: "pointer",
            color: colors.textSecondary,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1 }}>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 22,
              fontWeight: 700,
              color: colors.text,
              margin: 0,
            }}
          >
            {data.subject}
          </h1>
          <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
            Sent {fmtTime(data.sentAt)} · {data.totalSent} recipients · {data.totalEvents} events tracked
          </div>
        </div>
        <button
          onClick={() => {
            const sentDate = data.sentAt ? data.sentAt.split("T")[0] : "";
            const params = new URLSearchParams();
            if (sentDate) params.set("from", sentDate);
            navigate(`/email-activity?${params}`);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 14px",
            background: "none",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            color: colors.textSecondary,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <Activity size={14} />
          Email Activity
        </button>
      </div>

      {/* Stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <StatCard icon={Mail} label="Total Sent" value={data.totalSent} />
        <StatCard icon={Eye} label="Unique Opens" value={data.uniqueOpens} />
        <StatCard label="Open Rate" value={`${data.openRate}%`} />
        <StatCard icon={MousePointer} label="Unique Clicks" value={data.uniqueClicks} />
        <StatCard label="Click Rate" value={`${data.clickRate}%`} />
        <StatCard label="Click-to-Open" value={`${data.clickToOpenRate}%`} />
      </div>

      {/* Engagement Depth */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: 20,
          marginBottom: 24,
        }}
      >
        <h3 style={{ fontFamily: fonts.heading, fontSize: 15, fontWeight: 700, margin: "0 0 12px", color: colors.text }}>
          Engagement Depth
        </h3>
        <DepthBar categories={data.depthCategories} totalSent={data.totalSent} />
      </div>

      {/* Forward Detection */}
      {data.forwardEstimate && data.forwardEstimate.likelyForwarded > 0 && (
        <div
          style={{
            background: `${colors.info}0a`,
            border: `1px solid ${colors.info}30`,
            borderRadius: 8,
            padding: "14px 20px",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 13,
            color: colors.text,
          }}
        >
          <Share2 size={16} color={colors.info} />
          <span>
            <strong>{data.forwardEstimate.likelyForwarded}</strong> recipient{data.forwardEstimate.likelyForwarded !== 1 ? "s" : ""} likely
            forwarded this newsletter, reaching an estimated{" "}
            <strong>{data.forwardEstimate.totalExtraReaders}</strong> additional reader{data.forwardEstimate.totalExtraReaders !== 1 ? "s" : ""}.
          </span>
        </div>
      )}

      {/* Poll Results */}
      {pollData?.polls?.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {pollData.polls.map((poll) => {
            return (
              <div
                key={poll.pollId}
                style={{
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  padding: 20,
                  marginBottom: 12,
                }}
              >
                <h3
                  style={{
                    fontFamily: fonts.heading,
                    fontSize: 15,
                    fontWeight: 700,
                    margin: "0 0 4px",
                    color: colors.text,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <BarChart2 size={14} /> Poll Results
                </h3>
                <div style={{ fontSize: 14, color: colors.text, fontWeight: 600, marginBottom: 14 }}>
                  {poll.question}
                </div>

                {/* Vote bars */}
                {poll.options.map((opt, i) => {
                  const count = poll.tally[i] || 0;
                  const pct = poll.totalVotes > 0 ? Math.round((count / poll.totalVotes) * 100) : 0;
                  return (
                    <div key={i} style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3 }}>
                        <span style={{ color: colors.text }}>{opt}</span>
                        <span style={{ color: colors.textSecondary, fontFamily: fonts.mono, fontSize: 12 }}>
                          {count} vote{count !== 1 ? "s" : ""} ({pct}%)
                        </span>
                      </div>
                      <div style={{ background: colors.bgHover, borderRadius: 4, height: 20, overflow: "hidden" }}>
                        <div
                          style={{
                            background: colors.info,
                            height: "100%",
                            width: `${pct}%`,
                            borderRadius: 4,
                            transition: "width 0.3s",
                            minWidth: count > 0 ? 4 : 0,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}

                <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 8 }}>
                  {poll.totalVotes} vote{poll.totalVotes !== 1 ? "s" : ""} total
                </div>

                {/* Voter breakdown */}
                {poll.voters.length > 0 && (
                  <details style={{ marginTop: 12 }}>
                    <summary style={{ fontSize: 12, color: colors.textSecondary, cursor: "pointer", fontWeight: 600 }}>
                      Individual votes ({poll.voters.length})
                    </summary>
                    <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 6 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                            <th style={{ textAlign: "left", padding: "4px 8px", color: colors.textSecondary, fontWeight: 600 }}>Email</th>
                            <th style={{ textAlign: "left", padding: "4px 8px", color: colors.textSecondary, fontWeight: 600 }}>Voted</th>
                            <th style={{ textAlign: "left", padding: "4px 8px", color: colors.textSecondary, fontWeight: 600 }}>When</th>
                          </tr>
                        </thead>
                        <tbody>
                          {poll.voters.map((v, vi) => (
                            <tr key={vi} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                              <td style={{ padding: "4px 8px", color: colors.text }}>{v.email}</td>
                              <td style={{ padding: "4px 8px", color: colors.text }}>{v.option}</td>
                              <td style={{ padding: "4px 8px", color: colors.textSecondary }}>{fmtTime(v.votedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}

                {/* Free-text responses */}
                {poll.freeTextResponses.length > 0 && (
                  <div style={{ marginTop: 12, borderTop: `1px solid ${colors.border}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
                      <MessageSquare size={12} /> Written Responses ({poll.freeTextResponses.length})
                    </div>
                    {poll.freeTextResponses.map((r, ri) => (
                      <div
                        key={ri}
                        style={{
                          background: colors.bgHover,
                          borderRadius: 6,
                          padding: "8px 12px",
                          marginBottom: 6,
                          fontSize: 13,
                          color: colors.text,
                          borderLeft: `3px solid ${colors.info}`,
                        }}
                      >
                        <div>{r.text}</div>
                        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>
                          {r.email} · {fmtTime(r.votedAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Two-column layout: Link Performance + Top Contacts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Link Performance */}
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: 20,
          }}
        >
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 15,
              fontWeight: 700,
              margin: "0 0 12px",
              color: colors.text,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Link2 size={14} /> Link Performance
          </h3>
          {data.linkPerformance.length === 0 ? (
            <div style={{ fontSize: 13, color: colors.textSecondary, padding: "12px 0" }}>No clicks recorded yet.</div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <th style={{ textAlign: "left", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600 }}>URL</th>
                    <th style={{ textAlign: "right", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600, width: 60 }}>Clicks</th>
                    <th style={{ textAlign: "right", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600, width: 60 }}>Unique</th>
                  </tr>
                </thead>
                <tbody>
                  {data.linkPerformance.map((link, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                      <td
                        style={{ padding: "6px 8px", color: colors.textSecondary, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={link.url}
                      >
                        {truncateUrl(link.url)}
                      </td>
                      <td style={{ textAlign: "right", padding: "6px 8px", fontWeight: 600, color: colors.text }}>{link.totalClicks}</td>
                      <td style={{ textAlign: "right", padding: "6px 8px", color: colors.textSecondary }}>{link.uniqueClickers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top Engaged Contacts */}
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: 20,
          }}
        >
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 15,
              fontWeight: 700,
              margin: "0 0 12px",
              color: colors.text,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Users size={14} /> Top Engaged Contacts
          </h3>
          {data.topContacts.length === 0 ? (
            <div style={{ fontSize: 13, color: colors.textSecondary, padding: "12px 0" }}>No engagement recorded yet.</div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <th style={{ textAlign: "left", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600 }}>Email</th>
                    <th style={{ textAlign: "right", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600, width: 50 }}>Opens</th>
                    <th style={{ textAlign: "right", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600, width: 50 }}>Clicks</th>
                    <th style={{ textAlign: "center", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600, width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.topContacts.map((c, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                      <td style={{ padding: "6px 8px", color: colors.text, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.email}
                      </td>
                      <td style={{ textAlign: "right", padding: "6px 8px", color: colors.textSecondary }}>{c.opens}</td>
                      <td style={{ textAlign: "right", padding: "6px 8px", color: colors.textSecondary }}>{c.clicks}</td>
                      <td style={{ textAlign: "center", padding: "6px 8px" }}>
                        {c.forwardLikely && <Share2 size={12} color={colors.info} title={`Likely forwarded (${c.distinctIPs} IPs)`} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Engagement Timeline */}
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: 20,
        }}
      >
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 15,
            fontWeight: 700,
            margin: "0 0 12px",
            color: colors.text,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Clock size={14} /> Engagement Timeline
        </h3>
        {data.timeline.length === 0 ? (
          <div style={{ fontSize: 13, color: colors.textSecondary, padding: "12px 0" }}>No events recorded yet.</div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <th style={{ textAlign: "left", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600, width: 130 }}>Time</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600 }}>Email</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600, width: 70 }}>Event</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", color: colors.textSecondary, fontWeight: 600 }}>Link</th>
                </tr>
              </thead>
              <tbody>
                {[...data.timeline].reverse().map((evt, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                    <td style={{ padding: "6px 8px", color: colors.textSecondary, whiteSpace: "nowrap" }}>
                      {fmtTime(evt.timestamp)}
                    </td>
                    <td style={{ padding: "6px 8px", color: colors.text, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {evt.email}
                    </td>
                    <td style={{ padding: "6px 8px" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          color: evt.eventType === "open" ? colors.warning : colors.success,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {evt.eventType === "open" ? <Eye size={11} /> : <MousePointer size={11} />}
                        {evt.eventType}
                      </span>
                    </td>
                    <td
                      style={{ padding: "6px 8px", color: colors.textSecondary, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={evt.linkUrl || ""}
                    >
                      {evt.linkUrl ? truncateUrl(evt.linkUrl, 50) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
